import { useCallback, useRef, type Dispatch, type SetStateAction } from "react";
import { useQueryClient } from "@tanstack/react-query";

import {
  RouteBuilderProperty,
  RouteBuilderSession,
  RouteGenerationInputs,
} from "../../../../../types/routeBuilder";
import { multipleChoiceConfirmWrapper } from "../../../../../components/Modals/MultipleChoiceConfirmWrapper";
import routeBuilder from "../../../../../services/axios/routeBuilder/index";
import { endpointToCompanyLocation } from "../../../../../services/axios/routeBuilder/adapters";
import flash from "../../../../../services/flash";
import { invalidateQueriesContaining } from "../../../../../utils/queryInvalidation";
import { flashRouteBuilderError } from "../lib/flashRouteBuilderError";
import { confirmUnassignedProperties } from "../components/UnassignedPropertiesPrompt";
import type { ViewMode } from "../types/routeBuilderTypes";
import {
  incorporatesTime,
  maxAllowedRouteCount,
  unassignedFollowUp,
  unassignedMessage,
} from "../lib/helpers";
import {
  canApplyRouteBuilderSession,
  hasValidRouteEndpoints,
  isPersistedEditRoute,
} from "../lib/routeValidation";

type Args = {
  companyId: string;
  inputs: RouteGenerationInputs;
  properties: RouteBuilderProperty[];
  session: RouteBuilderSession | null;
  activeDraftId: string | null;
  setSession: Dispatch<SetStateAction<RouteBuilderSession | null>>;
  setInputs: Dispatch<SetStateAction<RouteGenerationInputs>>;
  setSelectedRouteId: (id: string | null) => void;
  setSelectedPropertyId: (id: string | null) => void;
  setDirtyRouteIds: Dispatch<SetStateAction<Set<string>>>;
  setHasUnsavedSessionChanges: Dispatch<SetStateAction<boolean>>;
  setFocusedRouteIds: Dispatch<SetStateAction<string[] | null>>;
  setMapHiddenRouteIds: Dispatch<SetStateAction<Set<string>>>;
  setIsBusy: Dispatch<SetStateAction<boolean>>;
  setViewMode?: (mode: ViewMode) => void;
  refreshDrafts: () => void;
  resetBuilder: () => void;
  onRoutesCreated?: () => void;
  onRoutesGenerated?: () => void;
  guardOptimization: () => boolean;
  handleOptimizationError: (err: unknown, fallbackPrefix: string) => void;
  refreshAccountUsage: () => void;
};

export function useRouteBuilderGenerate({
  companyId,
  inputs,
  properties,
  session,
  activeDraftId,
  setSession,
  setInputs,
  setSelectedRouteId,
  setSelectedPropertyId,
  setDirtyRouteIds,
  setHasUnsavedSessionChanges,
  setFocusedRouteIds,
  setMapHiddenRouteIds,
  setIsBusy,
  setViewMode,
  refreshDrafts,
  resetBuilder,
  onRoutesCreated,
  onRoutesGenerated,
  guardOptimization,
  handleOptimizationError,
  refreshAccountUsage,
}: Args) {
  const queryClient = useQueryClient();
  // Block repeated clicks during both the confirmation and the HTTP request.
  const generationInFlight = useRef(false);
  const optimizationGuardRef = useRef(guardOptimization);
  optimizationGuardRef.current = guardOptimization;
  const generate = useCallback(
    async (routeNames?: string[]) => {
      if (generationInFlight.current || !guardOptimization()) return;
      const generationInputs = inputs;
      const generationProperties = properties;
      if (generationProperties.length === 0) {
        void flash.error("Select at least one property before generating.");
        return;
      }
      const start = endpointToCompanyLocation(
        generationInputs.startLocation,
        companyId,
        "Start",
      );
      const end = endpointToCompanyLocation(
        generationInputs.endLocation,
        companyId,
        "End",
      );
      if (!generationInputs.plannedDispatchTime) {
        void flash.error("Select a planned dispatch time before generating.");
        return;
      }
      if (
        generationInputs.routeType === "temporary" &&
        !generationInputs.expiresAt
      ) {
        void flash.error("Select an expiration date before generating.");
        return;
      }
      if (!start || !end) {
        void flash.error("Select start and end locations before generating.");
        return;
      }
      if (!companyId) {
        void flash.error("Select a company before generating.");
        return;
      }
      if (
        !Number.isInteger(generationInputs.routeCount) ||
        generationInputs.routeCount < 1
      ) {
        void flash.error("Select at least one route before generating.");
        return;
      }
      if (
        incorporatesTime(generationInputs) &&
        generationProperties.some(
          (property) =>
            !Number.isFinite(property.estimatedServiceTimeSeconds) ||
            property.estimatedServiceTimeSeconds < 0,
        )
      ) {
        void flash.error(
          "Enter a valid estimated service time before generating.",
        );
        return;
      }
      generationInFlight.current = true;
      setIsBusy(true);
      try {
        // Ask when replacing a plan, so simply opening configuration preserves edits.
        if (session) {
          const confirmed = await multipleChoiceConfirmWrapper(
            "Generate routes with this configuration?",
            {
              description:
                "This will replace the current plan, including route adjustments. Your current plan will stay in place if generation fails.",
              confirmLabel: "Generate routes",
            },
          );
          if (!confirmed || !optimizationGuardRef.current()) return;
        }
        // Keep the current plan and edits intact until generation succeeds.
        const generationParams = {
          inputs: {
            ...generationInputs,
            routeCount: Math.min(
              generationInputs.routeCount,
              maxAllowedRouteCount(generationProperties.length),
            ),
          },
          properties: generationProperties,
          start,
          end,
          routeNamePrefix: routeNames?.[0]?.replace(/\s+\d+$/, "") ?? "Crew",
          companyId,
        };
        // Retry only the setup selection, never the broader Unassigned catalog.
        for (const withoutMaxRouteDuration of [false, true]) {
          const next = await routeBuilder.generatePlan({
            ...generationParams,
            withoutMaxRouteDuration,
          });
          setInputs(next.inputs);
          setSession(next);
          setSelectedRouteId(next.routes[0]?.id ?? null);
          setSelectedPropertyId(null);
          setDirtyRouteIds(new Set());
          setHasUnsavedSessionChanges(true);
          setFocusedRouteIds(null);
          setMapHiddenRouteIds(new Set());
          // Keep the draft link when generating after changes to its setup.
          setViewMode?.("split");
          onRoutesGenerated?.();
          refreshAccountUsage();
          const followUp = unassignedFollowUp({
            unassignedCount: next.unroutedJobs.length,
            limitedByMaxDuration:
              incorporatesTime(generationInputs) && !withoutMaxRouteDuration,
          });
          if (followUp === "none") break;
          const message = unassignedMessage(followUp, {
            unassignedCount: next.unroutedJobs.length,
            maxRouteDurationMinutes: generationInputs.maxRouteDurationMinutes,
          });
          const confirmed = await confirmUnassignedProperties(
            message,
            followUp === "offer-without-max",
          );
          if (
            followUp === "inform" ||
            !confirmed ||
            !optimizationGuardRef.current()
          )
            break;
        }
      } catch (err) {
        console.error(err);
        handleOptimizationError(err, "Failed to generate routes");
      } finally {
        generationInFlight.current = false;
        setIsBusy(false);
      }
    },
    [
      inputs,
      properties,
      session,
      companyId,
      setSession,
      setInputs,
      setSelectedRouteId,
      setSelectedPropertyId,
      setDirtyRouteIds,
      setHasUnsavedSessionChanges,
      setFocusedRouteIds,
      setMapHiddenRouteIds,
      setViewMode,
      setIsBusy,
      onRoutesGenerated,
      guardOptimization,
      handleOptimizationError,
      refreshAccountUsage,
    ],
  );

  const createRoutesFromBuilder = useCallback(async () => {
    if (!session || session.routes.length === 0) return null;
    const isEdit = session.builderMode === "edit";
    if (session.routes.some((route) => route.stops.length === 0)) {
      void flash.error(
        isEdit
          ? "Add at least one property to every route before updating routes."
          : "Add at least one property to every route before creating routes.",
      );
      return null;
    }
    if (
      session.routes.some(
        (route) =>
          !isPersistedEditRoute(session, route) &&
          !hasValidRouteEndpoints(route),
      )
    ) {
      void flash.error(
        isEdit
          ? "Select start and end locations for every new route before updating routes."
          : "Select start and end locations for every route before creating routes.",
      );
      return null;
    }
    if (!canApplyRouteBuilderSession(session)) return null;
    setIsBusy(true);
    try {
      await routeBuilder.applyPlan({ session, companyId }, setSession);
      const count = session.routes.length;
      if (activeDraftId) {
        await routeBuilder.deleteDraft(activeDraftId);
        refreshDrafts();
      }
      void flash.success(
        isEdit ? `Updated ${count} route(s).` : `Created ${count} route(s).`,
      );
      resetBuilder();
      onRoutesCreated?.();
      return { createdCount: count };
    } catch (err) {
      console.error(err);
      flashRouteBuilderError(
        err,
        isEdit ? "Failed to update routes" : "Failed to create routes",
      );
      return null;
    } finally {
      if (
        session.availableProperties.some(
          (property) =>
            (property.notes ?? "") !== (property.originalNotes ?? ""),
        )
      ) {
        void invalidateQueriesContaining(queryClient, ["job", "properties"]);
      }
      setIsBusy(false);
    }
  }, [
    session,
    companyId,
    activeDraftId,
    refreshDrafts,
    resetBuilder,
    onRoutesCreated,
    queryClient,
    setSession,
    setIsBusy,
  ]);

  return { generate, createRoutesFromBuilder };
}
